<template>
    <section class="pix-payment-view">
        <HeaderComponent :title="'Pagamento por Pix'" />

        <div class="pix-container">
            <div class="loading" v-if="loading">
                <h2>Gerando QRCode</h2>
                <i class="fa fa-spinner fa-spin fa-2x fa-fw"></i>
            </div>
            <div class="qrcode" v-else-if="qrCodeText">
                <div class="container">
                    <VueQRCodeComponent :text="qrCodeText" />
                </div>

                <div class="copy-pix" v-if="!loading && !error">
                    <input type="text" readonly :value="qrCodeText" ref="pixCodeInput" />
                    <button @click="copyPixCode">Copiar código</button>
                </div>

                <h2 v-if="!error && isInvoiceMode">
                    Finalize o pagamento pelo aplicativo do seu banco. O código é válido por {{ pixTimeout }} minutos.
                </h2>
                <h2 v-else-if="!error">
                    Finalize o pagamento pelo aplicativo do seu banco. O código é válido por {{ pixTimeout }} minutos.
                    A recarga será creditada automaticamente após a confirmação do pagamento, mesmo com o aplicativo fechado.
                </h2>

                <button
                    v-if="!isInvoiceMode && !error"
                    class="finish-button"
                    @click="goHome"
                >
                    Concluir
                </button>
            </div>
            <h2 class="error" v-else-if="error">
                Ocorreu um erro ao gerar o QRCode. Tente novamente mais tarde.
            </h2>
        </div>

        <ModalInfo v-if="isInvoiceMode" :modalOpen="modalOpen" @closeModal="finishRecharge" />
    </section>
</template>

<script>
import HeaderComponent from '@/components/header/HeaderComponent.vue'
import VueQRCodeComponent from 'vue-qrcode-component'
import ModalInfo from '@/components/modals/ModalInfo.vue'

import { getQrcodePix, checkPixInvoicePayment } from '@/components/controllers/PixRechargeController.js'
import { showSuccessNotification } from '@/libs/Util'
import { mapGetters, mapActions } from 'vuex'
import { log } from '@/libs/Logger'

export default {
    name: 'PixPaymentView',
    components: {
        HeaderComponent,
        VueQRCodeComponent,
        ModalInfo
    },
    data () {
        return {
            qrCodeText: '',
            loading: false,
            error: false,
            modalOpen: false,
            pixTimeout: 0
        }
    },
    methods: {
        ...mapActions(['showModalInfo']),
        copyPixCode: async function () {
            // tem que funcionar no mobile
            if (navigator.clipboard) {
                await navigator.clipboard.writeText(this.qrCodeText)
                showSuccessNotification({
                    title: '',
                    text: 'Código copiado com sucesso'
                })
            } else { // fallback para navegadores antigos e mobile
                this.$refs.pixCodeInput.select()
                document.execCommand('copy')
                showSuccessNotification({
                    title: '',
                    text: 'Código copiado com sucesso'
                })
            }
        },
        checkPixInvoicePayment: async function (nit, pixTimeout) {
            try {
                const paymentVerified = await checkPixInvoicePayment(
                    this.idBoletos, nit, pixTimeout, this.invoiceTotalValue
                )

                if (!paymentVerified?.error && paymentVerified) {
                    this.showModalInfo({
                        type: 'default',
                        text: 'Pagamento confirmado com sucesso',
                        icon: 'fa-solid fa-circle-check'
                    })
                    this.modalOpen = true
                    showSuccessNotification({ title: '', text: 'Pagamento realizado com sucesso' })
                    setTimeout(() => {
                        this.finishRecharge()
                    }, 4000)
                } else if (paymentVerified?.error) {
                    this.showModalInfo({
                        type: 'default',
                        text: paymentVerified.message,
                        icon: 'fa-solid fa-circle-xmark'
                    })
                    this.modalOpen = true
                }
            } catch (error) {
                log({ type: 'error', message: error?.message })
            }
        },
        finishRecharge: function () {
            this.modalOpen = false
            this.goHome()
        },
        goHome: function () {
            this.$router.push('/home')
        }
    },
    computed: {
        ...mapGetters(['totalRechargeValue', 'rechargeValues', 'isInvoiceMode', 'invoiceTotalValue',
            'idBoletos'
        ])
    },
    async mounted () {
        this.loading = true

        let res = null

        if (this.isInvoiceMode) {
            res = await getQrcodePix(this.invoiceTotalValue, this.isInvoiceMode, this.idBoletos)
        } else {
            res = await getQrcodePix(this.totalRechargeValue, false, undefined, this.rechargeValues)
        }

        if (!res.error) {
            this.qrCodeText = res.qrCode
            this.pixTimeout = res.pixTimeout / 60000 // timeout em milisegundos, transformar em minutos
            this.loading = false

            if (this.isInvoiceMode) {
                this.checkPixInvoicePayment(res.nit, res.pixTimeout)
            }
        } else {
            this.loading = false
            this.error = true
        }
    }
}
</script>

<style lang="less">
.pix-payment-view {

    .pix-container {
        height: calc(100vh - 75px);
        overflow-y: auto;

        &::-webkit-scrollbar {
            display: none;
        }
    }

    .loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        height: 100%;

        i {
            margin-top: 20px;
        }
    }

    .error {
        display: flex;
        flex-direction: column;
        align-items: center;
        height: 100%;
        padding: 0px 20px;
        text-align: center;
    }

    .qrcode {
        display: flex;
        justify-content: center;
        margin: 0px 20px;
        flex-direction: column;

        .container {
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 20px;
        }

        h2 {
            text-align: center;
            margin-top: 20px;
            font-size: 20px;
        }
    }

    .copy-pix {
        margin-top: 20px;
        display: flex;
        flex-direction: column;
        align-items: center;

        input {
            padding: 10px;
            font-size: 18px;
            border-radius: 5px;
            border: 1px solid #636363;
        }

        button {
            margin-top: 15px;
            border: 1px solid var(--primaryColor);
            border-radius: 5px;
            background-color: #fff;
            padding: 10px 20px;
            color: var(--primaryColor);
            cursor: pointer;
        }
    }

    .finish-button {
        margin: 24px auto 0;
        display: block;
        border: 1px solid var(--primaryColor);
        border-radius: 5px;
        background-color: var(--primaryColor);
        padding: 12px 28px;
        color: #fff;
        font-size: 16px;
        cursor: pointer;
    }

}
</style>
